/**
 * CCF-13 (#413) — duplicate-session freeze overlay.
 *
 * When the Bank gateway signals that this ONLINE session's online card number is
 * already active in another agent's desktop session (`authSessionCount` >= 2),
 * the BFF freezes the session server-side (every route 423s). This overlay is
 * the agent-facing half: a full-screen, non-dismissible modal that presents the
 * "WARNING - DO NOT PROCEED" copy, names the OTHER agent(s), shows the timestamp
 * of this login attempt and the next steps — so NEITHER agent can proceed with
 * any process while the block is active.
 *
 * There is deliberately NO "dismiss"/"proceed" affordance: the freeze is lifted
 * only by a Clear Session (server-side), after the Team Lead has given
 * clarification.
 *
 * #506 — the overlay covers the whole shell (incl. the AgentBanner's Clear
 * Session button), so it now carries its OWN "Clear session" button wired to the
 * same server-side clear-session handler. This is the sanctioned CCF-13
 * resolution (a server-side Clear Session), NOT a client-side dismiss/bypass —
 * hence still no close/proceed affordance.
 */

import { useEffect, useState } from 'react';
import errorIcon from '../assets/error-icon.svg';
import {
  getDuplicateSessionBlock,
  subscribeDuplicateSessionBlock,
} from '../duplicate-session-store.js';
import type { DuplicateSessionBlock } from '../session.js';
import { Button } from './ui/button.js';

/** Format the attempt timestamp for display; falls back to the raw value. */
function formatAttempt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function DuplicateSessionOverlay({
  onClearSession,
}: {
  /** Invokes the same server-side clear-session action as the banner (#506). */
  onClearSession: () => void;
}): JSX.Element | null {
  const [block, setBlock] = useState<DuplicateSessionBlock | null>(
    getDuplicateSessionBlock(),
  );
  useEffect(() => subscribeDuplicateSessionBlock(setBlock), []);

  if (!block) return null;

  const others = block.agents.length > 0 ? block.agents.join(', ') : undefined;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="ccf13-title"
      aria-describedby="ccf13-message"
      data-testid="duplicate-session-overlay"
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4"
    >
      <div className="max-w-[560px] w-full bg-white border-2 border-[#dc2626] p-6 flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <img
            src={errorIcon}
            alt=""
            aria-hidden="true"
            className="w-7 h-7 mt-0.5 shrink-0"
          />
          <h2
            id="ccf13-title"
            className="text-[18px] font-['Arial',sans-serif] font-bold text-[#dc2626] m-0"
          >
            Session frozen — do not proceed
          </h2>
        </div>
        <p
          id="ccf13-message"
          className="text-[14px] font-['Arial',sans-serif] text-[#1D252C] m-0 leading-[1.55]"
        >
          {block.message}
        </p>
        {others && (
          <p className="text-[14px] font-['Arial',sans-serif] text-[#1D252C] m-0">
            <span className="font-bold">Also in use with:</span> {others}
          </p>
        )}
        <p className="text-[14px] font-['Arial',sans-serif] text-[#1D252C] m-0">
          <span className="font-bold">Login attempt:</span>{' '}
          {formatAttempt(block.attemptedAt)}
        </p>
        <p className="text-[14px] font-['Arial',sans-serif] text-[#1D252C] m-0 leading-[1.55]">
          <span className="font-bold">Next steps:</span> seek the attention of
          your Team Lead and the other agent immediately. Do not continue with
          this call until clarification is given. Use Clear Session once the
          matter is resolved.
        </p>
        <Button
          type="button"
          onClick={onClearSession}
          aria-label="Clear session"
          data-testid="duplicate-session-clear"
          className="self-start bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
        >
          Clear session
        </Button>
      </div>
    </div>
  );
}
